import { ClientUser, ELDType, GeocodingSuggestion, LoadGroups } from '@numeo/types-core'
import { TaskStatus } from '@numeo/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TourType } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/components/types'

/** A tab of the Ongoing Loads settings modal that can be opened directly (it opens on Notifications otherwise). */
export type UpdaterSettingsModalTab = 'tags'

interface AddressCorrectionState {
    isOpen: boolean
    loadId: string | null
    stopIndex: number
    originalAddress: string
    suggestions: GeocodingSuggestion[]
}

export interface PreFilledEmailData {
    subject?: string
    body?: string
    toRecipients?: string[]
    ccRecipients?: string[]
    source?: 'broker_email_agent' | 'manual'
    taskId?: string
    threadId?: string
}

interface SplitLoadModalState {
    open: boolean
    loadId: string | null
    readOnly?: boolean
}

interface OngoingLoadsState {
    fileUploadModalOpen: boolean
    assignTruckModalOpen: boolean
    assignTrailerModalOpen: boolean
    rateConsModalOpen: boolean
    emailSideBarOpen: boolean
    taskSideBarOpen: boolean
    emailSidebarShowBackButton: boolean
    emailSidebarLoadId: string | null
    updaterSettingsModalOpen: boolean
    updaterSettingsModalTab: UpdaterSettingsModalTab | null
    driverCommunicationExpanded: boolean
    notificationTypeExpanded: boolean
    currentTour: TourType
    driverCommunicationTourStepIndex: number
    trucksSelectionModalOpen: boolean
    truckAssignmentMenuOpenForDriverId: string | null
    addDriverFormOpen: boolean
    driverFormOpen: boolean
    selectedDriver: ClientUser | null
    isAnyEldConnected: boolean
    addTruckMenuOpen: boolean
    firstELDTourModalOpen: boolean
    isVehiclesLoading: boolean
    isSetupCompleted: boolean
    addDriverMenuOpen: boolean
    shouldStartTour: boolean
    emailTourActionsActive: boolean
    firstDriverId: string | null
    isRateConsLoading: boolean
    theLoadGroup: Partial<LoadGroups> | null
    addressCorrection: AddressCorrectionState
    preFilledEmailData?: PreFilledEmailData
    taskSidebarFocusTaskId: string | null
    taskSidebarActiveTab: 'active' | 'completed' | 'archived' | 'all'
    pendingEldImportType: ELDType | null
    splitLoadModal: SplitLoadModalState
}

const initialState: OngoingLoadsState = {
    fileUploadModalOpen: false,
    assignTruckModalOpen: false,
    assignTrailerModalOpen: false,
    rateConsModalOpen: false,
    emailSideBarOpen: false,
    taskSideBarOpen: false,
    emailSidebarShowBackButton: false,
    emailSidebarLoadId: null,
    updaterSettingsModalOpen: false,
    updaterSettingsModalTab: null,
    driverCommunicationExpanded: false,
    notificationTypeExpanded: false,
    currentTour: null,
    driverCommunicationTourStepIndex: 0,
    trucksSelectionModalOpen: false,
    addDriverFormOpen: false,
    truckAssignmentMenuOpenForDriverId: null,
    driverFormOpen: false,
    selectedDriver: null,
    isAnyEldConnected: false,
    addTruckMenuOpen: false,
    firstELDTourModalOpen: false,
    isVehiclesLoading: false,
    isSetupCompleted: false,
    addDriverMenuOpen: false,
    shouldStartTour: false,
    emailTourActionsActive: false,
    firstDriverId: null,
    isRateConsLoading: false,
    theLoadGroup: null,
    addressCorrection: {
        isOpen: false,
        loadId: null,
        stopIndex: 0,
        originalAddress: '',
        suggestions: [],
    },
    preFilledEmailData: undefined,
    taskSidebarFocusTaskId: null,
    taskSidebarActiveTab: 'active',
    pendingEldImportType: null,
    splitLoadModal: { open: false, loadId: null },
}

const ongoingLoadsSlice = createSlice({
    name: 'ongoingLoads',
    initialState,
    reducers: {
        setFileUploadModalOpen(state, action: PayloadAction<boolean>) {
            state.fileUploadModalOpen = action.payload
        },
        setAssignTruckModalOpen(state, action: PayloadAction<boolean>) {
            state.assignTruckModalOpen = action.payload
        },
        setAssignTrailerModalOpen(state, action: PayloadAction<boolean>) {
            state.assignTrailerModalOpen = action.payload
        },
        setRateConsModalOpen(state, action: PayloadAction<boolean>) {
            state.rateConsModalOpen = action.payload
        },
        setEmailSideBarOpen(state, action: PayloadAction<boolean>) {
            state.emailSideBarOpen = action.payload
            if (!action.payload) {
                state.emailSidebarShowBackButton = false
            }
        },
        setTaskSideBarOpen(state, action: PayloadAction<boolean>) {
            state.taskSideBarOpen = action.payload
        },
        setEmailSidebarShowBackButton(state, action: PayloadAction<boolean>) {
            state.emailSidebarShowBackButton = action.payload
        },
        setEmailSidebarLoadId(state, action: PayloadAction<string | null>) {
            state.emailSidebarLoadId = action.payload
        },
        setUpdaterSettingsModalOpen(state, action: PayloadAction<boolean>) {
            state.updaterSettingsModalOpen = action.payload
            if (!action.payload) state.updaterSettingsModalTab = null
        },
        openUpdaterSettingsModalOnTab(state, action: PayloadAction<UpdaterSettingsModalTab>) {
            state.updaterSettingsModalOpen = true
            state.updaterSettingsModalTab = action.payload
        },
        setDriverCommunicationExpanded(state, action: PayloadAction<boolean>) {
            state.driverCommunicationExpanded = action.payload
        },
        setNotificationTypeExpanded(state, action: PayloadAction<boolean>) {
            state.notificationTypeExpanded = action.payload
        },
        setCurrentTour(state, action: PayloadAction<TourType>) {
            state.currentTour = action.payload
        },
        setDriverCommunicationTourStepIndex(state, action: PayloadAction<number>) {
            state.driverCommunicationTourStepIndex = action.payload
        },
        setTrucksSelectionModalOpen(state, action: PayloadAction<boolean>) {
            state.trucksSelectionModalOpen = action.payload
        },
        setAddDriverFormOpen(state, action: PayloadAction<boolean>) {
            state.addDriverFormOpen = action.payload
        },
        setDriverFormOpen(state, action: PayloadAction<boolean>) {
            state.driverFormOpen = action.payload
        },
        setSelectedDriver(state, action: PayloadAction<ClientUser | null>) {
            state.selectedDriver = action.payload
        },
        setIsAnyEldConnected(state, action: PayloadAction<boolean>) {
            state.isAnyEldConnected = action.payload
        },
        setAddTruckMenuOpen(state, action: PayloadAction<boolean>) {
            state.addTruckMenuOpen = action.payload
        },
        setFirstELDTourModalOpen(state, action: PayloadAction<boolean>) {
            state.firstELDTourModalOpen = action.payload
        },
        setIsVehiclesLoading(state, action: PayloadAction<boolean>) {
            state.isVehiclesLoading = action.payload
        },
        setIsSetupCompleted(state, action: PayloadAction<boolean>) {
            state.isSetupCompleted = action.payload
        },
        setAddDriverMenuOpen(state, action: PayloadAction<boolean>) {
            state.addDriverMenuOpen = action.payload
        },
        setShouldStartTour(state, action: PayloadAction<boolean>) {
            state.shouldStartTour = action.payload
        },
        setEmailTourActionsActive(state, action: PayloadAction<boolean>) {
            state.emailTourActionsActive = action.payload
        },
        setTruckAssignmentMenuOpenForDriverId(state, action: PayloadAction<string | null>) {
            state.truckAssignmentMenuOpenForDriverId = action.payload
        },
        setFirstDriverId(state, action: PayloadAction<string | null>) {
            state.firstDriverId = action.payload
        },
        setIsRateConsLoading(state, action: PayloadAction<boolean>) {
            state.isRateConsLoading = action.payload
        },
        setTheLoadGroup(state, action: PayloadAction<Partial<LoadGroups> | null>) {
            state.theLoadGroup = action.payload
        },
        openAddressCorrectionModal(
            state,
            action: PayloadAction<{ loadId: string; stopIndex: number; originalAddress: string; suggestions: GeocodingSuggestion[] }>
        ) {
            state.addressCorrection = {
                isOpen: true,
                loadId: action.payload.loadId,
                stopIndex: action.payload.stopIndex,
                originalAddress: action.payload.originalAddress,
                suggestions: action.payload.suggestions,
            }
        },
        closeAddressCorrectionModal(state) {
            state.addressCorrection = {
                isOpen: false,
                loadId: null,
                stopIndex: 0,
                originalAddress: '',
                suggestions: [],
            }
        },
        setPreFilledEmailData(state, action: PayloadAction<PreFilledEmailData | undefined>) {
            state.preFilledEmailData = action.payload
        },
        setTaskSidebarFocusTaskId(state, action: PayloadAction<string | null>) {
            state.taskSidebarFocusTaskId = action.payload
        },
        setTaskSidebarActiveTab(state, action: PayloadAction<OngoingLoadsState['taskSidebarActiveTab']>) {
            state.taskSidebarActiveTab = action.payload
        },
        setPendingEldImportType(state, action: PayloadAction<ELDType | null>) {
            state.pendingEldImportType = action.payload
        },
        setSplitLoadModalOpen(state, action: PayloadAction<SplitLoadModalState>) {
            state.splitLoadModal = action.payload
        },
        setTaskSidebarActiveTabByStatus(state, action: PayloadAction<TaskStatus | null | undefined>) {
            switch (action.payload) {
                case TaskStatus.COMPLETED:
                    state.taskSidebarActiveTab = 'completed'
                    break
                case TaskStatus.ARCHIVED:
                    state.taskSidebarActiveTab = 'archived'
                    break
                case TaskStatus.ACTIVE:
                    state.taskSidebarActiveTab = 'active'
                    break
                default:
                    state.taskSidebarActiveTab = 'all'
            }
        },
    },
})

export const {
    setAssignTruckModalOpen,
    setAssignTrailerModalOpen,
    setCurrentTour,
    setEmailSideBarOpen,
    setTaskSideBarOpen,
    setEmailSidebarShowBackButton,
    setUpdaterSettingsModalOpen,
    openUpdaterSettingsModalOnTab,
    setDriverCommunicationExpanded,
    setNotificationTypeExpanded,
    setTrucksSelectionModalOpen,
    setAddDriverFormOpen,
    setDriverFormOpen,
    setIsAnyEldConnected,
    setAddTruckMenuOpen,
    setFirstELDTourModalOpen,
    setIsVehiclesLoading,
    setAddDriverMenuOpen,
    setShouldStartTour,
    setEmailTourActionsActive,
    setTruckAssignmentMenuOpenForDriverId,
    setFirstDriverId,
    setIsRateConsLoading,
    openAddressCorrectionModal,
    closeAddressCorrectionModal,
    setPreFilledEmailData,
    setTaskSidebarFocusTaskId,
    setTaskSidebarActiveTab,
    setTaskSidebarActiveTabByStatus,
    setPendingEldImportType,
    setSplitLoadModalOpen,
} = ongoingLoadsSlice.actions

export const selectAddressCorrection = (state: { ongoingLoads: OngoingLoadsState }) => state.ongoingLoads.addressCorrection

export default ongoingLoadsSlice.reducer
